import React, { useState, useEffect } from 'react';
import './App.css';
import SearchForm from './components/SearchForm';
import Preview from './components/Preview';
import History from './components/History';
import ThemeToggle from './components/ThemeToggle';
import Playlist from './components/Playlist';

// In production, default to same-origin backend (empty string = relative URLs).
// REACT_APP_API_URL can override in development (e.g., 'http://localhost:8082').
const API_URL = process.env.REACT_APP_API_URL || '';

function App() {
  const [activeTab, setActiveTab] = useState('search');
  const [currentMeta, setCurrentMeta] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  
  const getThemeFromTime = () => {
    const hour = new Date().getHours();
    // Day mode: 6 AM to 6 PM, Night mode: 6 PM to 6 AM
    return (hour >= 6 && hour < 18) ? 'light' : 'dark';
  };

  const [theme, setTheme] = useState(() => {
    // Always start with time-based theme
    return getThemeFromTime();
  });
  
  const [manualOverride, setManualOverride] = useState(false);

  useEffect(() => {
    loadHistory();
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Auto-switch theme based on time (runs always)
  useEffect(() => {
    const checkTime = () => {
      const newTheme = getThemeFromTime();
      // Only auto-switch if not manually overridden, or if we're at a time boundary
      if (!manualOverride) {
        if (newTheme !== theme) {
          setTheme(newTheme);
        }
      } else {
        // Reset manual override at time boundaries (6 AM and 6 PM)
        const hour = new Date().getHours();
        if (hour === 6 || hour === 18) {
          setManualOverride(false);
          setTheme(newTheme);
        }
      }
    };
    
    // Check every minute
    const interval = setInterval(checkTime, 60000);
    return () => clearInterval(interval);
  }, [manualOverride, theme]);

  const toggleTheme = () => {
    setManualOverride(true);
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const loadHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/history`);
      if (response.ok) {
        const data = await response.json();
        const loaded = data.history || [];
        setHistory(loaded);
        localStorage.setItem('ytmp3_history', JSON.stringify(loaded));
      }
    } catch (e) {
      console.error('Failed to load history:', e);
      // Fallback to localStorage if backend not available
      const local = localStorage.getItem('ytmp3_history');
      setHistory(local ? JSON.parse(local) : []);
    }
  };

  const saveHistory = async (newItem) => {
    try {
      const response = await fetch(`${API_URL}/history`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newItem),
      });
      if (response.ok) {
        const data = await response.json();
        const updated = data.history || [];
        setHistory(updated);
        localStorage.setItem('ytmp3_history', JSON.stringify(updated));
      }
    } catch (e) {
      console.error('Failed to save history:', e);
      // still store locally so History tab works offline
      const current = [...history, newItem].slice(-50);
      setHistory(current);
      localStorage.setItem('ytmp3_history', JSON.stringify(current));
    }
  };

  const handleSearch = async (url) => {
    if (!url) {
      showStatus('Please enter a YouTube URL', 'error');
      return;
    }

    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/;
    if (!youtubeRegex.test(url)) {
      showStatus('Please enter a valid YouTube URL', 'error');
      return;
    }

    setLoading(true);
    showStatus('Fetching details...', 'loading');

    try {
      const response = await fetch(`${API_URL}/info?url=${encodeURIComponent(url)}`);
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Unable to fetch info');
      }
      const data = await response.json();
      setCurrentMeta(data);
      showStatus('Ready to download!', 'success');
    } catch (error) {
      setCurrentMeta(null);
      showStatus(`Error: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!currentMeta) return;

    setLoading(true);
    showStatus('Converting to MP3...', 'loading');

    try {
      const url = `${API_URL}/download?url=${encodeURIComponent(currentMeta.url)}${
        currentMeta.cover ? `&cover=${encodeURIComponent(currentMeta.cover)}` :
        (currentMeta.thumbnail ? `&cover=${encodeURIComponent(currentMeta.thumbnail)}` : '')
      }`;

      const response = await fetch(url);
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Download failed');
      }

      const blob = await response.blob();
      const filename = `${currentMeta.safe_title || 'audio'}.mp3`;

      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);

      showStatus('✓ Download successful!', 'success');

      // Add to history
      const newItem = {
        title: currentMeta.title,
        artist: currentMeta.artist,
        url: currentMeta.url,
        cover: currentMeta.cover || currentMeta.thumbnail || '',
        duration: currentMeta.duration,
        timestamp: Date.now(),
      };
      await saveHistory(newItem);
    } catch (error) {
      showStatus(`Error: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleHistoryClick = (item) => {
    setActiveTab('search');
    handleSearch(item.url);
  };

  const handleClearHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/history`, { method: 'DELETE' });
      if (response.ok) {
        setHistory([]);
      }
    } catch (e) {
      console.error('Failed to clear history:', e);
    }
  };

  const showStatus = (msg, type) => {
    setStatus({ message: msg, type });
    if (type === 'success' || type === 'error') {
      setTimeout(() => setStatus(null), 4500);
    }
  };

  return (
    <>
      <ThemeToggle theme={theme} onToggle={toggleTheme} />
      <div className="app">
        <div className="orb orb-1"></div>
        <div className="orb orb-2"></div>
        <div className="orb orb-3"></div>
        <div className="orb orb-4"></div>
        <div className="orb orb-5"></div>
        <div className="orb orb-6"></div>
        <div className="orb orb-7"></div>
        <div className="orb orb-8"></div>
        <div className="orb orb-9"></div>
        <div className="container">
          <header className="header">
            <h1>🎵 YouTube to MP3</h1>
            <p className="subtitle">
              Transform your favorite videos into <span className="subtitle-highlight">high-quality MP3</span> files with 
              clean metadata, album artwork, and intelligent title cleaning
            </p>
          </header>

          {/* Tab Navigation */}
          <div className="tabs">
            <button
              className={`tab-button ${activeTab === 'search' ? 'active' : ''}`}
              onClick={() => setActiveTab('search')}
            >
              🎵 Search
            </button>
            <button
              className={`tab-button ${activeTab === 'playlist' ? 'active' : ''}`}
              onClick={() => setActiveTab('playlist')}
            >
              📋 Playlist
            </button>
            <button
              className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
              onClick={() => setActiveTab('history')}
            >
              📱 History
            </button>
          </div>

          {/* Tab Content */}
          <div className="tab-content">
            {activeTab === 'search' && (
              <>
                <SearchForm
                  onSearch={handleSearch}
                  onDownload={handleDownload}
                  loading={loading}
                  status={status}
                  currentMeta={currentMeta}
                />
                {currentMeta && (
                  <Preview meta={currentMeta} />
                )}
              </>
            )}

            {activeTab === 'playlist' && (
              <Playlist onSaveHistory={saveHistory} />
            )}

            {activeTab === 'history' && (
              <History
                items={history}
                onItemClick={handleHistoryClick}
                onClear={handleClearHistory}
              />
            )}
          </div>
        </div>
        <footer className="footer">
          <div className="footer-content">
            <span>Made with</span>
            <span className="heart">♥</span>
          </div>
        </footer>
      </div>
    </>
  );
}

function Status({ message, type }) {
  return <div className={`status status-${type}`}>{message}</div>;
}

export default App;
